import React from "react";
import CardLessDataTableWithSearch from "../../datatables/CardLessDataTableWithSearch";

const SUBJECT_COLORS = [
    { bar: "#3b82f6", bg: "#eff6ff", text: "#1d4ed8" },
    { bar: "#10b981", bg: "#ecfdf5", text: "#047857" },
    { bar: "#8b5cf6", bg: "#f5f3ff", text: "#6d28d9" },
    { bar: "#f59e0b", bg: "#fffbeb", text: "#b45309" },
    { bar: "#ec4899", bg: "#fdf2f8", text: "#be185d" },
];

export function SubjectPerformanceCards({ subjects }) {
    if (!subjects || subjects.length === 0) {
        return <p className="text-muted text-center py-3 mb-0">No subject data available</p>;
    }
    return (
        <div className="row g-3">
            {subjects.map((s, idx) => {
                const colorTheme = SUBJECT_COLORS[idx % SUBJECT_COLORS.length];
                return (
                    <div className="col-12 col-sm-6 col-lg-4" key={s.subjectId || idx}>
                        <div
                            style={{
                                background: "#fff",
                                border: "1px solid #e2e8f0",
                                borderTop: `4px solid ${colorTheme.bar}`,
                                borderRadius: 12,
                                padding: "16px",
                                height: "100%",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-3px)";
                                e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.08)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "none";
                                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.03)";
                            }}
                        >
                            {/* Card Header */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="font-weight-bold mb-0" style={{ fontSize: 15, color: "#1e293b" }}>
                                    <i className="fal fa-book-alt me-2" style={{ color: colorTheme.bar }} />
                                    {s.subjectName}
                                </h6>
                                <span style={{ background: colorTheme.bg, color: colorTheme.text, fontWeight: 700, fontSize: 11.5, padding: "2px 8px", borderRadius: 12 }}>
                                    {s.totalQuestions} Questions
                                </span>
                            </div>

                            {/* Stat Highlights */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                                <div style={{ background: "#f8fafc", padding: "8px 10px", borderRadius: 8, border: "1px solid #f1f5f9" }}>
                                    <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Avg Marks</div>
                                    <div style={{ fontSize: 16, fontWeight: 800, color: "#16a34a", marginTop: 2 }}>{s.averageMarks} pts</div>
                                </div>
                                <div style={{ background: "#f8fafc", padding: "8px 10px", borderRadius: 8, border: "1px solid #f1f5f9" }}>
                                    <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Avg Accuracy</div>
                                    <div style={{ fontSize: 16, fontWeight: 800, color: "#0284c7", marginTop: 2 }}>{s.averageAccuracy}%</div>
                                </div>
                            </div>

                            {/* Detailed List */}
                            <div className="d-flex flex-column" style={{ gap: 6, fontSize: 12 }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <span style={{ color: "#64748b", fontWeight: 600 }}>Marks (High / Low)</span>
                                    <span style={{ fontWeight: 700, color: "#334155" }}>{s.highestMarks} / {s.lowestMarks}</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <span style={{ color: "#64748b", fontWeight: 600 }}>Accuracy (High / Low)</span>
                                    <span style={{ fontWeight: 700, color: "#334155" }}>{s.highestAccuracy}% / {s.lowestAccuracy}%</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <span style={{ color: "#64748b", fontWeight: 600 }}>Right / Wrong</span>
                                    <span>
                                        <span style={{ color: "#16a34a", fontWeight: 700 }}>✓ {s.rightAnswers}</span>
                                        <span style={{ margin: "0 4px", color: "#cbd5e1" }}>|</span>
                                        <span style={{ color: "#dc2626", fontWeight: 700 }}>✕ {s.wrongAnswers}</span>
                                    </span>
                                </div>

                                {s.topStudent && (
                                    <div className="d-flex justify-content-between align-items-center mt-1" style={{ background: "#f0fdf4", padding: "4px 8px", borderRadius: 6, border: "1px solid #bbf7d0" }}>
                                        <span style={{ fontSize: 11, color: "#15803d", fontWeight: 700 }}>🏆 Top Student</span>
                                        <span style={{ fontSize: 11.5, fontWeight: 700, color: "#16a34a" }} className="text-truncate" title={s.topStudent}>{s.topStudent}</span>
                                    </div>
                                )}
                                {s.lowestStudent && (
                                    <div className="d-flex justify-content-between align-items-center" style={{ background: "#fef2f2", padding: "4px 8px", borderRadius: 6, border: "1px solid #fecaca" }}>
                                        <span style={{ fontSize: 11, color: "#b91c1c", fontWeight: 700 }}>⚠️ Needs Help</span>
                                        <span style={{ fontSize: 11.5, fontWeight: 700, color: "#dc2626" }} className="text-truncate" title={s.lowestStudent}>{s.lowestStudent}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

const subjectComparisonColumns = [
    {
        dataField: "subjectName",
        text: "Subject",
        sort: true,
        formatter: (cell) => (
            <span style={{ fontWeight: 800, color: "#1e293b", fontSize: 13 }}>
                <i className="fal fa-book-alt me-1 text-primary" />
                {cell}
            </span>
        ),
    },
    {
        dataField: "averageMarks",
        text: "Avg Marks",
        sort: true,
        formatter: (cell) => (
            <span style={{ fontWeight: 800, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "2px 8px", borderRadius: 6, fontSize: 12 }}>
                {cell} pts
            </span>
        ),
    },
    {
        dataField: "highestMarks",
        text: "Highest",
        sort: true,
        formatter: (cell) => <span style={{ fontWeight: 700, color: "#16a34a" }}>↑ {cell}</span>,
    },
    {
        dataField: "lowestMarks",
        text: "Lowest",
        sort: true,
        formatter: (cell) => <span style={{ fontWeight: 700, color: "#dc2626" }}>↓ {cell}</span>,
    },
    {
        dataField: "highestAccuracy",
        text: "Highest Accuracy",
        sort: true,
        formatter: (cell) => <span style={{ fontWeight: 700, color: "#0369a1", background: "#f0f9ff", border: "1px solid #bae6fd", padding: "2px 8px", borderRadius: 6, fontSize: 11.5 }}>{cell}%</span>,
    },
    {
        dataField: "lowestAccuracy",
        text: "Lowest Accuracy",
        sort: true,
        formatter: (cell) => <span style={{ fontWeight: 700, color: "#c2410c", background: "#fff7ed", border: "1px solid #ffedd5", padding: "2px 8px", borderRadius: 6, fontSize: 11.5 }}>{cell}%</span>,
    },
    {
        dataField: "averageRank",
        text: "Avg Rank",
        sort: true,
        formatter: (cell) => <span style={{ fontWeight: 700, color: "#475569" }}>#{cell || "-"}</span>,
    },
];

export function SubjectComparisonTable({ subjects }) {
    if (!subjects || subjects.length === 0) {
        return <p className="text-muted text-center py-3 mb-0">No subject data available</p>;
    }
    return (
        <CardLessDataTableWithSearch
            parentData={subjects}
            particlecolumns={subjectComparisonColumns}
            defaultSorted={[{ dataField: "averageMarks", order: "desc" }]}
            name="subject_comparison"
            placeholder="Search subjects..."
        />
    );
}

function SubjectComparison({ subjects }) {
    return (
        <>
            <SubjectPerformanceCards subjects={subjects} />
            <div className="mt-3">
                <SubjectComparisonTable subjects={subjects} />
            </div>
        </>
    );
}

export default SubjectComparison;
